import type { ActionLogFilters } from '@/types';

/**
 * Query keys, built in one place.
 *
 * Hand-written arrays scattered through components are how cache invalidation
 * quietly stops working - `['customers']` in one file and `['customer']` in
 * another look the same in review and are different caches.
 *
 * The nesting means a broad key invalidates everything under it:
 * `queryClient.invalidateQueries({ queryKey: queryKeys.customers.all })` clears
 * both the list and every detail.
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    mfa: () => [...queryKeys.auth.all, 'mfa'] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: (attention: number) => [...queryKeys.dashboard.all, 'summary', attention] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.customers.all, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
    seatHistory: (seatId: string, limit: number) =>
      [...queryKeys.customers.all, 'seat-history', seatId, limit] as const,
  },
  staff: {
    all: ['staff'] as const,
    salesReps: () => [...queryKeys.staff.all, 'sales-reps'] as const,
  },
  actionLog: {
    all: ['action-log'] as const,
    list: (filters: ActionLogFilters, offset: number) =>
      [...queryKeys.actionLog.all, 'list', filters, offset] as const,
    actors: () => [...queryKeys.actionLog.all, 'actors'] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
} as const;
